import { Icon } from '@phosphor-icons/react'
import React, { ReactNode } from 'react'
import { z } from 'zod'

import { ButtonUseKeys } from '@/components/ui/button'
import {
  DropdownMenuItem,
  DropdownMenuVariant,
} from '@/components/ui/dropdown-menu/dropdown-menu.types'
import { SelectOptionAction } from '@/components/ui/select/select.types'
import { Colors } from '@/theme/defaults'

export const SortOrderSchema = z.enum(['asc', 'desc'])

export type SortOrder = z.infer<typeof SortOrderSchema>

export type TableMode = 'regular' | 'bulk'

export interface SortState {
  sortBy: string | null
  sortOrder: SortOrder | null
}

export interface TableColumn<T = unknown> {
  title: string
  // Note: Stable identity for this column, independent of `title`. Used for
  // localStorage persistence (visibility, pinning) and React keys via
  // `getColumnId` - falls back to `title` when omitted. Any column whose
  // `title` comes from `t()` MUST set an explicit `id`: a translated title
  // changes when the user's language changes, and without a stable `id` that
  // silently invalidates persisted visibility/pinning state (columns
  // "disappearing" after a language switch, since the stored English title no
  // longer matches any current column).
  id?: string
  // Note: Width is static to make the fixed layout of table.
  // It prevents layout shifting when loading or empty state.
  // If some item doesn't fit - the text is truncated and tooltip is added.
  // By default, the width is 15rem. It's the business width (title + expected cell content) -
  // the table only grows it when the title plus the header's unpin / sort / "..." actions don't
  // fit, see getColumnWidth.
  width?: string
  render: (item: T) => ReactNode
  // Note: If provided, the column becomes sortable. The sortKey is sent to the API.
  sortKey?: string
  // Note: Custom labels for sort options in dropdown menu
  sortAscLabel?: string
  sortDescLabel?: string
  // Note: If provided, an info icon will be shown next to the column title with this content as tooltip
  tooltip?: ReactNode
  // Note: If provided, column will be hidden be default but its visibility can still be toggled via table options
  hiddenByDefault?: boolean
  // Note: If true, the column is excluded from the column-visibility picker entirely (no
  // toggle for it at all) and always rendered - use for a trailing actions column (delete,
  // approve/decline) that the table can't meaningfully function without.
  excludeFromVisibilityControl?: boolean
  // Note: For simple string/number cell content, wraps up to this many lines before
  // truncating with an ellipsis (with a hover tooltip for the full text), instead of the
  // default single-line truncation.
  breakLines?: number | boolean
  // Note: Extra items appended to the column's "More column actions" menu. Receives the rendered
  // (visible, pin-ordered) columns and the current page's rows, e.g. for a settings modal preview.
  getMenuItems?: (context: TableColumnMenuContext<T>) => DropdownMenuItem[]
  // Note: A trailing action for this column's row in the column-visibility picker (e.g. "Settings").
  // Gets the same context as getMenuItems.
  getVisibilityAction?: (context: TableColumnMenuContext<T>) => SelectOptionAction
  // Note: Briefly flashes the column's cells (e.g. right after its settings were saved)
  highlighted?: boolean
}

export interface TableColumnMenuContext<T = unknown> {
  columns: TableColumn<T>[]
  data: T[]
}

// Note: Stable column identity for localStorage keys / React keys - see
// TableColumn.id above.
export const getColumnId = (column: Pick<TableColumn, 'id' | 'title'>): string =>
  column.id ?? column.title

export interface BulkAction<T> {
  icon?: Icon
  iconColor?: keyof Colors
  // Note: Label is optional - if not provided, only icon will be shown
  label?: string
  hidden?: (selectedItems: T[]) => boolean
  use?: ButtonUseKeys
  onlyBox?: boolean
  onClick: (selectedItems: T[]) => void
  disabled?: (selectedItems: T[]) => boolean
  tooltip?: (selectedItems: T[]) => string | undefined
}

export interface RowAction<T = unknown> {
  // Note: Primary action means that this action will be called on entire row click
  primary?: boolean
  variant?: DropdownMenuVariant
  icon?: Icon
  label: string
  onClick: (item: T) => void
  disabled?: (item: T) => boolean
  hidden?: (item: T) => boolean
  loading?: (item: T) => boolean
}

export interface SelectAllMatchingConfig<T> {
  // Note: total rows matching the current filter across ALL pages - typically just
  // pagination.total, already computed by the consumer for Pagination.
  total: number
  // Note: called once when the header checkbox is checked. Must resolve to every matching row
  // as full T objects (not just IDs) - BulkAction.onClick / TableBulkMenu need real objects,
  // same as the page-scoped default. The consumer owns the actual fetch (e.g. a second
  // paginated call with the same filters and limit=total).
  resolveAll: (signal?: AbortSignal) => Promise<T[]>
}

export interface TableProps<T> {
  className?: string
  columns: TableColumn<T>[]
  data: T[]
  emptyText?: string
  emptyDescription?: string
  error?: Error | null
  getRowKey: (item: T) => string | number
  loading?: boolean
  // Note: Unique identifier for localStorage persistence of pinned column
  id?: string
  // Note: Controls whether column pinning is available in the table UI. Works in bulk mode too -
  // the checkbox column becomes sticky alongside the pinned column. Only columns with a string
  // `title` are pinnable (ghost/action-only columns are skipped).
  enablePinning?: boolean
  mode?: TableMode
  // Note: Optional renderer for collapsable row content under each main row.
  // Return a ReactNode to make the row collapsable, or null/undefined for regular rows.
  renderCollapsableRow?: (item: T, index: number) => ReactNode
  sortBy?: string | null
  sortOrder?: SortOrder | null
  onSort?: (sortBy: string | null, sortOrder: SortOrder | null) => void
  bulkActions?: BulkAction<T>[]
  rowActions?: RowAction<T>[]
  onClearFilters?: () => void
  // Note: opts this table into cross-page "select all matching the current filter" behavior on
  // the header checkbox. Omitted (default): selection stays current-page-only, unchanged for
  // every existing bulk-mode consumer.
  selectAllMatching?: SelectAllMatchingConfig<T>
}

export interface TableBodyProps {
  children: React.ReactNode
  columnsAmount: number
  loading?: boolean
  emptyText?: string
  emptyDescription?: string
  error?: Error | null
  onClearFilters?: () => void
}

export interface TableHeadProps<T = unknown> {
  columns: TableColumn<T>[]
  // Note: by column id - see Table's columnWidths.
  columnWidths: Record<string, string>
  data: T[]
  pinnedColumnId: string | null
  onPinColumn: (columnId: string | null) => void
  enablePinning?: boolean
  sortBy?: string | null
  sortOrder?: SortOrder | null
  onSort?: (sortBy: string | null, sortOrder: SortOrder | null) => void
  mode?: TableMode
  isAllSelected?: boolean
  isIndeterminate?: boolean
  // Note: true while an active selectAllMatching.resolveAll() call is in flight - disables the
  // header checkbox and shows a small spinner next to it.
  isSelectAllLoading?: boolean
  onSelectAll?: (checked: boolean) => void
}
